import { useEffect, useRef, useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { ExternalLink, UserX } from "lucide-react";
import { api, ApiError, type Me } from "@/data";
import { keys, useAuthConfig } from "@/data/queries";
import { landingFor } from "@/domain/rbac";
import { safeHref } from "@/domain/validation";
import { useTheme } from "@/app/theme";
import { Button } from "@/components/ui/Button";
import { Field, Input } from "@/components/ui/Field";
import { LoadingRows } from "@/components/ui/States";
import { LanguageSwitch } from "@/components/LanguageSwitch";
import loginWebp960 from "@/assets/login-960.webp";
import loginWebp1600 from "@/assets/login-1600.webp";
import loginJpg960 from "@/assets/login-960.jpg";
import loginJpg1600 from "@/assets/login-1600.jpg";
import { currentLang, tr } from "@/i18n/i18n";

interface GoogleId {
  initialize(o: { client_id: string; callback: (r: { credential: string }) => void; ux_mode?: "popup"; auto_select?: boolean }): void;
  renderButton(el: HTMLElement, o: Record<string, unknown>): void;
}
declare global {
  interface Window {
    google?: { accounts: { id: GoogleId } };
  }
}

const GSI_SRC = "https://accounts.google.com/gsi/client";

function loadGsi(): Promise<GoogleId> {
  if (window.google?.accounts?.id) return Promise.resolve(window.google.accounts.id);
  return new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = GSI_SRC;
    s.async = true;
    s.onload = () => (window.google?.accounts?.id ? resolve(window.google.accounts.id) : reject(new Error("Google sign-in failed to load")));
    s.onerror = () => reject(new Error("Google sign-in failed to load. Check your connection."));
    document.head.appendChild(s);
  });
}

export default function LoginPage() {
  const config = useAuthConfig();
  const { theme } = useTheme();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const buttonRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<{ message: string; notRegistered?: boolean } | null>(null);
  const [busy, setBusy] = useState(false);
  const [devEmail, setDevEmail] = useState("");
  const [pwEmail, setPwEmail] = useState("");
  const [password, setPassword] = useState("");

  const done = (user: Me) => {
    qc.clear();
    qc.setQueryData(keys.me, user);
    const next = params.get("next");
    navigate(next && next.startsWith("/") && !next.startsWith("//") ? next : landingFor(user), { replace: true });
  };
  const fail = (e: unknown) => {
    if (e instanceof ApiError && e.code === "NOT_REGISTERED") setError({ message: tr(e.message), notRegistered: true });
    else setError({ message: e instanceof Error ? tr(e.message) : tr("Sign-in failed.") });
  };

  const clientId = config.data?.googleClientId;
  useEffect(() => {
    if (!clientId || !buttonRef.current) return;
    let cancelled = false;
    loadGsi()
      .then((gsi) => {
        if (cancelled || !buttonRef.current) return;
        gsi.initialize({
          client_id: clientId,
          ux_mode: "popup",
          callback: async ({ credential }) => {
            setBusy(true);
            setError(null);
            try {
              done(await api.auth.google(credential));
            } catch (e) {
              fail(e);
            } finally {
              setBusy(false);
            }
          },
        });
        buttonRef.current.replaceChildren();
        // Fit the button to the card: a fixed 320px button made small phones zoom the whole page out.
        const width = Math.max(200, Math.min(320, Math.floor(buttonRef.current.getBoundingClientRect().width) || 320));
        gsi.renderButton(buttonRef.current, { theme: theme === "dark" ? "filled_black" : "outline", size: "large", shape: "pill", text: "signin_with", width, logo_alignment: "center", locale: currentLang() });
      })
      .catch((e) => !cancelled && setError({ message: tr(e.message) }));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, theme]);

  const passwordSignIn = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      done(await api.auth.password(pwEmail, password));
    } catch (err) {
      setPassword("");
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const devSignIn = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      done(await api.auth.dev(devEmail));
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const formUrl = safeHref(config.data?.registerFormUrl);

  return (
    <main className="relative grid min-h-dvh bg-bg lg:grid-cols-[minmax(0,1.15fr)_minmax(420px,1fr)]">
      {/* Editorial photo: banner on phones, full-height panel on desktop. */}
      <div className="relative h-56 overflow-hidden sm:h-72 lg:h-auto">
        <picture>
          <source type="image/webp" srcSet={`${loginWebp960} 960w, ${loginWebp1600} 1600w`} sizes="(min-width: 1024px) 55vw, 100vw" />
          <img
            src={loginJpg1600}
            srcSet={`${loginJpg960} 960w, ${loginJpg1600} 1600w`}
            sizes="(min-width: 1024px) 55vw, 100vw"
            alt=""
            width={1600}
            height={903}
            fetchPriority="high"
            className="absolute inset-0 size-full object-cover object-[60%_30%]"
          />
        </picture>
        <div className="absolute inset-0 bg-gradient-to-t from-bg/80 via-bg/10 to-transparent lg:bg-gradient-to-r lg:from-transparent lg:via-transparent lg:to-bg/30" aria-hidden />
        <p className="absolute bottom-5 left-6 font-display text-lg font-semibold tracking-[0.3em] text-white drop-shadow lg:bottom-10 lg:left-10 lg:text-2xl">ORDER AI</p>
      </div>

      <div className="relative flex items-center justify-center px-4 pb-10 pt-16 lg:px-12 lg:py-10">
        <LanguageSwitch className="absolute right-4 top-4" />
      <div className="w-full max-w-md">
        <div className="rounded-lg border border-border bg-surface p-5 shadow-sm sm:p-8">
          <h1 data-screen-title className="text-xl font-semibold">{tr("Sign in")}</h1>
          <p className="mt-1 text-sm text-muted">{tr("Use the Google account you registered with.")}</p>

          {config.isLoading ? (
            <LoadingRows rows={1} />
          ) : (
            <div className="mt-6 flex flex-col items-center gap-4">
              {/* color-scheme: light keeps the Google iframe transparent in dark mode (no white box). */}
              {clientId && <div ref={buttonRef} style={{ colorScheme: "light" }} className="flex min-h-11 w-full max-w-full justify-center overflow-hidden" aria-busy={busy} />}
              {!clientId && !config.data?.devLogin && (
                <p role="alert" className="rounded-md border border-warning/30 bg-warning-subtle p-3 text-sm text-fg">
                  {tr("Google sign-in is not configured on this server yet.")}
                </p>
              )}
              {busy && <p className="text-sm text-muted" role="status">{tr("Signing you in…")}</p>}
            </div>
          )}

          {error && (
            <div role="alert" className="mt-5 rounded-md border border-danger/30 bg-danger-subtle p-3 text-sm">
              <p className="flex items-start gap-2 font-medium text-danger">
                {error.notRegistered && <UserX className="mt-0.5 size-4 shrink-0" aria-hidden />}
                {error.message}
              </p>
              {error.notRegistered && (
                <p className="mt-1 text-fg">{tr("Register once with the sign-up form, then sign in again (it can take a minute to appear).")}</p>
              )}
            </div>
          )}

          {config.data?.passwordLogin && (
            <form onSubmit={passwordSignIn} className="mt-6 flex flex-col gap-3" noValidate>
              <div className="flex items-center gap-3 text-xs uppercase tracking-wide text-muted" aria-hidden>
                <span className="h-px flex-1 bg-border" />
                {tr("or")}
                <span className="h-px flex-1 bg-border" />
              </div>
              <Field label={tr("Email")}>
                <Input type="email" value={pwEmail} onChange={(e) => setPwEmail(e.target.value)} autoComplete="username" />
              </Field>
              <Field label={tr("Password")}>
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
              </Field>
              <Button type="submit" variant="secondary" loading={busy} disabled={!pwEmail.trim() || !password}>
                {tr("Sign in with password")}
              </Button>
            </form>
          )}

          {config.data?.devLogin && (
            <form onSubmit={devSignIn} className="mt-6 flex flex-col gap-3 border-t border-border pt-5" noValidate>
              <p className="text-xs font-medium uppercase tracking-wide text-warning">{tr("Development server · email sign-in")}</p>
              <Field label={tr("Registered email")}>
                <Input type="email" value={devEmail} onChange={(e) => setDevEmail(e.target.value)} autoComplete="email" />
              </Field>
              <Button type="submit" variant="secondary" loading={busy} disabled={!devEmail.trim()}>
                {tr("Sign in without Google")}
              </Button>
            </form>
          )}
        </div>
        <p className="mt-5 text-center text-sm text-muted">
          {tr("Not registered yet?")}{" "}
          {formUrl ? (
            <a href={formUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-medium text-accent hover:underline">
              {tr("Open the sign-up form")}
              <ExternalLink className="size-3.5" aria-hidden />
            </a>
          ) : (
            <span>{tr("Ask your leader for the sign-up form.")}</span>
          )}
        </p>
      </div>
      </div>
    </main>
  );
}
